import React, { useState, useEffect, useRef, useContext } from 'react';
import { useWebRTC } from './useWebRTC';
import GeneralContext from '../../../../utils/generalContext';
import { fetching, post } from '../../../../Generals';
import Loader from '../../../../Components/Loader';
import './InteractiveVideocall.css';

export const InteractiveVideocall = (props) => {
    const {
        data,
        itemid,
        id: itemAlumnoId,
        cursoid,
        userid,
        global,
        markedAsDone,
        toggleReloadData
    } = props;

    const { user } = useContext(GeneralContext);
    const studentUser = user?.user?.user || {};
    const moduloActual = data?.moduloactual || {};
    const itemData = moduloActual?.cursos_item || {};

    // Session / Flow State: 'lobby' | 'queue' | 'active_call' | 'completed'
    const [callPhase, setCallPhase] = useState(
        moduloActual.hecho && moduloActual.grade === '100' ? 'completed' : 'lobby'
    );
    const [meetingId, setMeetingId] = useState(null);
    const [assignedTeacher, setAssignedTeacher] = useState(null);
    const [waitingError, setWaitingError] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [activeDrawerTab, setActiveDrawerTab] = useState('chat'); // 'chat' | 'notes'
    const [chatInput, setChatInput] = useState('');
    const [sessionNotes, setSessionNotes] = useState('');
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [layoutMode, setLayoutMode] = useState('pip'); // 'pip' | 'split'

    const containerRef = useRef(null);
    const localVideoRef = useRef(null);
    const remoteVideoRef = useRef(null);
    const timerRef = useRef(null);
    const queuePollRef = useRef(null);

    // Unique Room ID for WebRTC: meetingId or deterministic course/item/student key
    const roomId = meetingId ? `bi_meet_${meetingId}` : `bi_room_c${cursoid}_i${itemid}_u${userid}`;

    const handleSessionFinished = (payload) => {
        if (queuePollRef.current) clearInterval(queuePollRef.current);
        endCall();
        const grade = payload?.grade ?? '100';
        if (String(grade) === '100') {
            setCallPhase('completed');
        } else {
            setWaitingError(payload?.comments || 'La sesión ha finalizado. El profesor indicó observaciones que requieren práctica adicional.');
            setCallPhase('lobby');
        }
        toggleReloadData?.();
    };

    const handleSessionCancelled = () => {
        if (queuePollRef.current) clearInterval(queuePollRef.current);
        endCall();
        setWaitingError('La sesión de videoconferencia fue cancelada por el profesor.');
        setCallPhase('lobby');
        toggleReloadData?.();
    };

    const handleLeaveCall = () => {
        if (queuePollRef.current) clearInterval(queuePollRef.current);
        const params = { userid };
        const url = `${global.domain}api/v1/closeOnWaitingList/${global.apicode}`;
        fetching(url, params, () => {
            endCall();
            setCallPhase('lobby');
            toggleReloadData?.();
        });
    };

    const {
        localStream,
        remoteStream,
        connectionState,
        isMuted,
        isVideoOff,
        isScreenSharing,
        isSpeaking,
        dataMessages,
        startLocalMedia,
        toggleMute,
        toggleVideo,
        startScreenShare,
        stopScreenShare,
        sendMessage,
        endCall
    } = useWebRTC({
        roomId,
        userId: userid,
        role: 'student',
        global,
        onSessionFinished: handleSessionFinished,
        onSessionCancelled: handleSessionCancelled
    });

    // 1. Ensure student is cleared from waiting queue when entering/mounting in lobby state
    useEffect(() => {
        if (callPhase === 'lobby' && userid && global?.domain && global?.apicode) {
            const params = { userid };
            const url = `${global.domain}api/v1/closeOnWaitingList/${global.apicode}`;
            fetching(url, params, () => {}, null);
        }
        return () => {
            if (userid && global?.domain && global?.apicode) {
                const params = { userid };
                const url = `${global.domain}api/v1/closeOnWaitingList/${global.apicode}`;
                fetching(url, params, () => {}, null);
            }
        };
    }, []);

    // 2. Attach Local Media Stream to Video Element
    useEffect(() => {
        if (localVideoRef.current && localStream) {
            localVideoRef.current.srcObject = localStream;
        }
    }, [localStream, callPhase]);

    // 2. Attach Remote (Teacher) Stream to Video Element
    useEffect(() => {
        if (remoteVideoRef.current && remoteStream) {
            remoteVideoRef.current.srcObject = remoteStream;
        }
    }, [remoteStream, callPhase]);

    // 3. Call Duration Timer
    useEffect(() => {
        if (callPhase === 'active_call') {
            timerRef.current = setInterval(() => {
                setElapsedSeconds((prev) => prev + 1);
            }, 1000);
        } else {
            if (timerRef.current) clearInterval(timerRef.current);
            setElapsedSeconds(0);
        }
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [callPhase]);

    // 4. Queue / Meeting Status Polling
    const checkMeetingStatus = async (mId) => {
        if (!mId) return;
        const params = {
            cursoid,
            userid,
            meetingid: mId
        };
        const url = `${global.domain}api/v1/checkStatusMeetingRoom/${global.apicode}`;
        fetching(url, params, (res) => {
            if (res && res.profesor) {
                setAssignedTeacher(res.profesor);
                setCallPhase('active_call');
            }
            if (res && res.terminado) {
                if (queuePollRef.current) clearInterval(queuePollRef.current);
                endCall();
                toggleReloadData?.();
            }
        });
    };

    const joinWaitingQueue = async () => {
        setWaitingError(null);
        try {
            // Start local camera/mic preview
            await startLocalMedia();

            const params = {
                cursoid,
                userid,
                itemid
            };
            const url = `${global.domain}api/v1/consultaData/${global.apicode}`;
            fetching(url, params, (res) => {
                if (res && res.error === 1) {
                    setWaitingError(res.errormsg || 'No tiene sesiones disponibles para esta videoconferencia.');
                } else if (res && res.meetingid) {
                    setMeetingId(res.meetingid);
                    setCallPhase('queue');

                    // Start polling for professor acceptance
                    if (queuePollRef.current) clearInterval(queuePollRef.current);
                    queuePollRef.current = setInterval(() => {
                        checkMeetingStatus(res.meetingid);
                    }, 3000);
                } else {
                    // Direct practice mode fallback
                    setCallPhase('active_call');
                }
            });
        } catch (err) {
            setWaitingError('No se pudo acceder a la cámara o micrófono. Por favor verifica los permisos.');
        }
    };

    const cancelQueue = () => {
        if (queuePollRef.current) clearInterval(queuePollRef.current);
        const params = { userid };
        const url = `${global.domain}api/v1/closeOnWaitingList/${global.apicode}`;
        fetching(url, params, () => {
            endCall();
            setCallPhase('lobby');
        });
    };

    const handleSendChatMessage = (e) => {
        e.preventDefault();
        if (!chatInput.trim()) return;
        sendMessage(chatInput.trim(), studentUser.Nombre || 'Alumno');
        setChatInput('');
    };

    const toggleFullscreen = () => {
        if (!containerRef.current) return;
        if (!document.fullscreenElement) {
            containerRef.current.requestFullscreen?.().catch(() => {});
            setIsFullscreen(true);
        } else {
            document.exitFullscreen?.().catch(() => {});
            setIsFullscreen(false);
        }
    };

    const formatTimer = (totalSeconds) => {
        const mins = Math.floor(totalSeconds / 60);
        const secs = totalSeconds % 60;
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    };

    // =========================================================================
    // RENDER: Phase 1 - Completed State
    // =========================================================================
    if (callPhase === 'completed') {
        return (
            <div className="bi-vc-container p-4 text-center">
                <div className="bi-vc-lobby-card mx-auto my-auto">
                    <div className="text-success mb-3" style={{ fontSize: '3rem' }}>
                        <i className="fas fa-check-circle"></i>
                    </div>
                    <h3 className="text-white mb-2">¡Videoconferencia Completada!</h3>
                    <p className="text-muted mb-4">
                        Tu sesión de práctica en vivo ha finalizado exitosamente y tu progreso ha sido registrado.
                    </p>
                    <div className="d-flex justify-content-center gap-3">
                        <button
                            className="btn btn-outline-info"
                            onClick={() => {
                                setCallPhase('lobby');
                                toggleReloadData?.();
                            }}
                        >
                            <i className="fas fa-redo mr-2"></i> Repetir práctica
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // =========================================================================
    // RENDER: Phase 2 - Lobby (Pre-call Preview & Availability Checks)
    // =========================================================================
    if (callPhase === 'lobby') {
        const sesionesDisponibles = data?.sesionesdisponibles ?? 1;
        const onlineProfesores = data?.onlineprofesores ?? true;

        if (sesionesDisponibles < 1) {
            return (
                <div className="alert alert-warning mt-3">
                    <p className="mb-0">
                        No tiene sesiones disponibles para tomar esta práctica. <a className="alert-link font-weight-bold" href="/comprar/1">Comprar sesiones</a>
                    </p>
                </div>
            );
        }

        if (!onlineProfesores) {
            return (
                <div className="alert alert-warning mt-3">
                    <p className="mb-0">En estos momentos no hay profesores conectados que puedan atenderle.</p>
                </div>
            );
        }

        return (
            <div className="bi-vc-container" ref={containerRef}>
                <div className="bi-vc-header">
                    <div className="bi-vc-title-group">
                        <span className="bi-vc-badge">
                            <i className="fas fa-video mr-1"></i> Videoconferencia WebRTC
                        </span>
                        <span className="font-weight-bold">{itemData.titulo || 'Práctica Oral en Vivo'}</span>
                    </div>
                </div>

                <div className="bi-vc-lobby">
                    <div className="bi-vc-lobby-card">
                        <h4 className="text-white mb-2">Preparación de Videoconferencia</h4>
                        <p className="text-muted small">
                            Asegúrate de que tu cámara y micrófono funcionen correctamente antes de iniciar.
                        </p>

                        <div className="bi-vc-preview-box">
                            {localStream ? (
                                <video
                                    ref={localVideoRef}
                                    autoPlay
                                    playsInline
                                    muted
                                    className="bi-vc-video-element"
                                />
                            ) : (
                                <div className="bi-vc-avatar-fallback h-100">
                                    <div className="bi-vc-avatar-circle">
                                        {(studentUser.Nombre || 'A')[0].toUpperCase()}
                                    </div>
                                    <small>Vista previa de cámara</small>
                                </div>
                            )}
                        </div>

                        {waitingError && (
                            <div className="alert alert-danger py-2 small mb-3">
                                <i className="fas fa-exclamation-triangle mr-2"></i> {waitingError}
                            </div>
                        )}

                        <div className="bi-vc-lobby-actions">
                            <button
                                className="btn btn-success btn-lg px-4 shadow"
                                onClick={joinWaitingQueue}
                            >
                                <i className="fas fa-phone-alt mr-2"></i> Iniciar Videoconferencia
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // =========================================================================
    // RENDER: Phase 3 - Waiting Queue
    // =========================================================================
    if (callPhase === 'queue') {
        return (
            <div className="bi-vc-container" ref={containerRef}>
                <div className="bi-vc-header">
                    <div className="bi-vc-title-group">
                        <span className="bi-vc-badge bi-vc-badge-waiting">
                            <span className="bi-vc-dot-pulse"></span> En Lista de Espera
                        </span>
                        <span>Sala #{meetingId}</span>
                    </div>
                </div>

                <div className="bi-vc-lobby">
                    <div className="bi-vc-lobby-card">
                        <div className="spinner-border text-info mb-3" style={{ width: '3rem', height: '3rem' }}></div>
                        <h4 className="text-white mb-2">Esperando a tu profesor...</h4>
                        <p className="text-muted small mb-4">
                            Estás en turno. En cuanto un profesor disponible acepte tu llamada, la videoconferencia comenzará automáticamente en esta ventana.
                        </p>

                        <div className="bi-vc-preview-box" style={{ maxWidth: '280px', height: '160px' }}>
                            <video
                                ref={localVideoRef}
                                autoPlay
                                playsInline
                                muted
                                className="bi-vc-video-element"
                            />
                        </div>

                        <div className="d-flex justify-content-center gap-3 mt-3">
                            <button className="btn btn-outline-danger" onClick={cancelQueue}>
                                <i className="fas fa-times mr-2"></i> Cancelar turno
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // =========================================================================
    // RENDER: Phase 4 - Active Interactive Videocall (WebRTC Connected)
    // =========================================================================
    return (
        <div className={`bi-vc-container ${isFullscreen ? 'fullscreen' : ''}`} ref={containerRef}>
            {/* Top Bar */}
            <div className="bi-vc-header">
                <div className="bi-vc-title-group">
                    <span className="bi-vc-badge">
                        <span className="bi-vc-dot-pulse"></span> En Vivo
                    </span>
                    <span className="bi-vc-timer">{formatTimer(elapsedSeconds)}</span>
                    {assignedTeacher && (
                        <span className="small text-muted d-none d-md-inline">
                            Profesor: <strong className="text-white">{assignedTeacher.nombre}</strong>
                        </span>
                    )}
                </div>

                <div className="d-flex align-items-center gap-2">
                    <button
                        className="btn btn-sm btn-dark"
                        onClick={() => setLayoutMode(layoutMode === 'pip' ? 'split' : 'pip')}
                        title="Cambiar vista (Picture in Picture / División)"
                    >
                        <i className={`fas ${layoutMode === 'pip' ? 'fa-columns' : 'fa-th-large'}`}></i>
                    </button>
                    <button
                        className="btn btn-sm btn-dark"
                        onClick={() => setIsDrawerOpen(!isDrawerOpen)}
                        title="Abrir Chat y Notas"
                    >
                        <i className="fas fa-comments mr-1"></i>
                        {dataMessages.length > 0 && (
                            <span className="badge badge-primary ml-1">{dataMessages.length}</span>
                        )}
                    </button>
                    <button
                        className="btn btn-sm btn-dark"
                        onClick={toggleFullscreen}
                        title="Pantalla Completa"
                    >
                        <i className={`fas ${isFullscreen ? 'fa-compress' : 'fa-expand'}`}></i>
                    </button>
                </div>
            </div>

            {/* Video Stage & Side Drawer */}
            <div className="bi-vc-stage">
                <div className={`bi-vc-video-grid connected ${layoutMode === 'split' ? 'split-view' : ''}`}>
                    {/* Remote (Teacher) Video Area */}
                    <div className="bi-vc-card w-100 h-100">
                        {remoteStream ? (
                            <video
                                ref={remoteVideoRef}
                                autoPlay
                                playsInline
                                className="bi-vc-video-element"
                            />
                        ) : (
                            <div className="bi-vc-avatar-fallback">
                                <div className="bi-vc-avatar-circle" style={{ background: '#059669' }}>
                                    <i className="fas fa-chalkboard-teacher"></i>
                                </div>
                                <h5 className="text-white mt-2">
                                    {assignedTeacher?.nombre || 'Profesor de Inglés'}
                                </h5>
                                <small className="text-muted">Conectando señal WebRTC...</small>
                            </div>
                        )}
                        <div className="bi-vc-name-tag">
                            <i className="fas fa-user-graduate mr-1"></i>
                            {assignedTeacher?.nombre || 'Profesor'}
                        </div>
                    </div>

                    {/* Local (Student) Video Card */}
                    <div className={`bi-vc-card bi-vc-card-local ${layoutMode === 'split' ? 'pip-disabled' : ''} ${isSpeaking ? 'speaking' : ''}`}>
                        {!isVideoOff && localStream ? (
                            <video
                                ref={localVideoRef}
                                autoPlay
                                playsInline
                                muted
                                className="bi-vc-video-element"
                            />
                        ) : (
                            <div className="bi-vc-avatar-fallback">
                                <div className="bi-vc-avatar-circle" style={{ width: '48px', height: '48px', fontSize: '1.2rem' }}>
                                    {(studentUser.Nombre || 'A')[0].toUpperCase()}
                                </div>
                            </div>
                        )}
                        <div className="bi-vc-name-tag">
                            <span>{studentUser.Nombre || 'Tú (Alumno)'}</span>
                            {isMuted && <i className="fas fa-microphone-slash text-danger ml-1"></i>}
                        </div>
                    </div>
                </div>

                {/* Side Drawer (Chat / Notes) */}
                {isDrawerOpen && (
                    <div className="bi-vc-drawer">
                        <div className="bi-vc-drawer-header">
                            <div className="bi-vc-drawer-tabs">
                                <button
                                    className={`bi-vc-tab-btn ${activeDrawerTab === 'chat' ? 'active' : ''}`}
                                    onClick={() => setActiveDrawerTab('chat')}
                                >
                                    Chat ({dataMessages.length})
                                </button>
                                <button
                                    className={`bi-vc-tab-btn ${activeDrawerTab === 'notes' ? 'active' : ''}`}
                                    onClick={() => setActiveDrawerTab('notes')}
                                >
                                    Notas
                                </button>
                            </div>
                            <button
                                className="btn btn-sm text-muted"
                                onClick={() => setIsDrawerOpen(false)}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        {activeDrawerTab === 'chat' ? (
                            <>
                                <div className="bi-vc-drawer-body">
                                    {dataMessages.length === 0 ? (
                                        <div className="text-center text-muted my-auto small">
                                            No hay mensajes en esta sesión.
                                        </div>
                                    ) : (
                                        dataMessages.map((msg, idx) => (
                                            <div
                                                key={idx}
                                                className={`bi-vc-msg-item ${msg.role === 'student' ? 'mine' : ''}`}
                                            >
                                                <div className="bi-vc-msg-header">
                                                    <strong>{msg.sender}</strong>
                                                    <span>{msg.timestamp}</span>
                                                </div>
                                                <div>{msg.text}</div>
                                            </div>
                                        ))
                                    )}
                                </div>
                                <form className="bi-vc-drawer-footer" onSubmit={handleSendChatMessage}>
                                    <input
                                        type="text"
                                        className="bi-vc-chat-input"
                                        placeholder="Escribe un mensaje..."
                                        value={chatInput}
                                        onChange={(e) => setChatInput(e.target.value)}
                                    />
                                    <button type="submit" className="btn btn-primary btn-sm">
                                        <i className="fas fa-paper-plane"></i>
                                    </button>
                                </form>
                            </>
                        ) : (
                            <div className="bi-vc-drawer-body">
                                <textarea
                                    className="form-control bg-dark text-white border-secondary h-100 p-2"
                                    placeholder="Escribe apuntes y vocabulario durante la clase..."
                                    value={sessionNotes}
                                    onChange={(e) => setSessionNotes(e.target.value)}
                                    style={{ resize: 'none', fontSize: '0.85rem' }}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Floating Bottom Toolbar */}
            <div className="bi-vc-controls">
                <button
                    className={`bi-vc-btn-control ${isMuted ? 'active-off' : ''}`}
                    onClick={toggleMute}
                    title={isMuted ? 'Activar Micrófono' : 'Silenciar Micrófono'}
                >
                    <i className={`fas ${isMuted ? 'fa-microphone-slash' : 'fa-microphone'}`}></i>
                </button>

                <button
                    className={`bi-vc-btn-control ${isVideoOff ? 'active-off' : ''}`}
                    onClick={toggleVideo}
                    title={isVideoOff ? 'Encender Cámara' : 'Apagar Cámara'}
                >
                    <i className={`fas ${isVideoOff ? 'fa-video-slash' : 'fa-video'}`}></i>
                </button>

                <button
                    className={`bi-vc-btn-control ${isScreenSharing ? 'active-on' : ''}`}
                    onClick={isScreenSharing ? stopScreenShare : startScreenShare}
                    title={isScreenSharing ? 'Dejar de compartir pantalla' : 'Compartir Pantalla'}
                >
                    <i className="fas fa-desktop"></i>
                </button>

                <button
                    className="bi-vc-btn-control bi-vc-btn-end"
                    onClick={handleLeaveCall}
                    title="Salir de la videoconferencia"
                >
                    <i className="fas fa-phone-slash"></i>
                </button>
            </div>
        </div>
    );
};

export default InteractiveVideocall;
